import { useEffect, useState } from "react";
import { Archive, CheckCircle2, Cloud, FlaskConical, Globe, MinusCircle, Network, Share2, Upload, type LucideIcon } from "lucide-react";
import { formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { slugFromLabel } from "@ncp/forms";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, InlineAlert, Input, NativeSelect, SaveError, Switch, Textarea, cn, saveFailureText } from "@ncp/ui";
import { useConnectors } from "../connectors/api";
import { DOCUMENT_KINDS, SNAPSHOT_COLLECTIONS, SYNCABLE_SOURCE_KINDS, useKnowledgeActions, type KnowledgeSource, type KnowledgeSourceKind, type KnowledgeSourcePreview } from "../workspaces/api";

type T = ReturnType<typeof useTranslation>["t"];
type Locale = ReturnType<typeof useLocale>["locale"];

export const SOURCE_ICON: Record<KnowledgeSourceKind, LucideIcon> = { url: Globe, crawl: Network, snapshot: Archive, sharepoint: Share2, onedrive: Cloud, upload: Upload };

/** Common cadences; anything else is kept as a custom cron expression. Schedules run in UAE time. */
export const SCHEDULE_PRESETS = [
  { id: "hourly", cron: "0 * * * *" },
  { id: "daily", cron: "0 3 * * *" },
  { id: "weekly", cron: "0 4 * * 1" },
  { id: "monthly", cron: "0 3 1 * *" },
] as const;

export function scheduleLabel(cron: string | null | undefined, t: T) {
  if (!cron) return t("admin:knowledge.sources.schedule.manual");
  const preset = SCHEDULE_PRESETS.find((p) => p.cron === cron);
  return preset ? t(`admin:knowledge.sources.schedule.${preset.id}`) : cron;
}

export function sourceLabel(source: KnowledgeSource, t: T) {
  return source.name?.trim() || source.key || t(`admin:knowledge.sources.kind.${source.kind}`);
}

const KEY_PATTERN = /^[a-z][a-z0-9-]{1,63}$/;
const MANAGED_CONFIG = ["urls", "url", "startUrls", "depth", "maxItems", "include", "exclude", "collection", "connectorKey", "siteUrl", "library", "user", "folder", "recursive", "kind", "issuer"];
const str = (v: unknown) => (typeof v === "string" ? v : "");
const list = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : typeof v === "string" ? [v] : []);
const lines = (v: string) => v.split(/\r?\n/).map((u) => u.trim()).filter(Boolean);
const isAddress = (u: string) => /^https:\/\/[^\s/]+/i.test(u) || u.startsWith("{{simulators}}/");

/**
 * Adds or edits one synchronised source of a collection. The source is kept in the collection's draft until the collection is saved;
 * "Test" lists what the draft would bring in without filing anything. Kind and key are fixed once the source is saved, because the
 * documents it filed remember both.
 */
export function SourceDialog({ collectionKey, source, saved, existingKeys, defaultKind, onSave, onClose }: {
  collectionKey: string;
  source: KnowledgeSource | null;
  saved: boolean;
  existingKeys: string[];
  defaultKind: string;
  onSave: (source: KnowledgeSource) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const actions = useKnowledgeActions();
  const connectors = useConnectors();
  const cfg = source?.config ?? {};

  const [kind, setKind] = useState<KnowledgeSourceKind>(source?.kind ?? "sharepoint");
  const [name, setName] = useState(source?.name ?? "");
  const [key, setKey] = useState(source?.key ?? "");
  const [keyTouched, setKeyTouched] = useState(!!source?.key);
  const [enabled, setEnabled] = useState(source?.enabled ?? true);
  const [schedule, setSchedule] = useState(source?.schedule ?? "");
  const [custom, setCustom] = useState(!!source?.schedule && !SCHEDULE_PRESETS.some((p) => p.cron === source.schedule));
  const [connectorKey, setConnectorKey] = useState(str(cfg.connectorKey) || "microsoft-graph");
  const [siteUrl, setSiteUrl] = useState(str(cfg.siteUrl));
  const [library, setLibrary] = useState(str(cfg.library) || "Documents");
  const [user, setUser] = useState(str(cfg.user));
  const [folder, setFolder] = useState(str(cfg.folder));
  const [recursive, setRecursive] = useState(cfg.recursive !== false);
  const [urls, setUrls] = useState([...list(cfg.urls), ...list(cfg.url)].join("\n"));
  const [startUrls, setStartUrls] = useState(list(cfg.startUrls).join("\n"));
  const [depth, setDepth] = useState(typeof cfg.depth === "number" ? cfg.depth : 1);
  const [maxItems, setMaxItems] = useState(String(typeof cfg.maxItems === "number" ? cfg.maxItems : 100));
  const [include, setInclude] = useState(list(cfg.include).join("\n"));
  const [exclude, setExclude] = useState(list(cfg.exclude).join("\n"));
  const [snapshotCollection, setSnapshotCollection] = useState(str(cfg.collection) || ((SNAPSHOT_COLLECTIONS as readonly string[]).includes(collectionKey) ? collectionKey : SNAPSHOT_COLLECTIONS[0]));
  const [docKind, setDocKind] = useState(str(cfg.kind) || defaultKind);
  const [issuer, setIssuer] = useState(str(cfg.issuer));
  const [preview, setPreview] = useState<KnowledgeSourcePreview | null>(null);

  const effectiveKey = keyTouched ? key : slugFromLabel(name || t(`admin:knowledge.sources.kind.${kind}`));
  const keyError = !effectiveKey ? t("admin:knowledge.sources.errors.keyRequired")
    : !KEY_PATTERN.test(effectiveKey) ? t("admin:knowledge.create.keyInvalid")
    : existingKeys.includes(effectiveKey) ? t("admin:knowledge.create.keyTaken") : null;
  const addressLines = lines(urls);
  const startLines = lines(startUrls);
  const badAddress = (kind === "crawl" ? startLines : addressLines).find((u) => !isAddress(u));
  const maxItemsValue = Number(maxItems);
  const maxItemsError = kind === "crawl" && !(Number.isInteger(maxItemsValue) && maxItemsValue >= 1 && maxItemsValue <= 300) ? t("admin:knowledge.sources.errors.maxItems") : null;
  const startError = kind === "crawl" && (startLines.length === 0 || startLines.length > 10) ? t("admin:knowledge.sources.errors.startUrls") : null;
  const cronError = custom && schedule && !/^\S+(\s+\S+){4}$/.test(schedule.trim()) ? t("admin:knowledge.sources.errors.cron") : null;

  const config: Record<string, unknown> = kind === source?.kind ? { ...(source.config ?? {}) } : {};
  for (const k of MANAGED_CONFIG) delete config[k];
  const put = (k: string, v: string) => { if (v.trim()) config[k] = v.trim(); };
  if (kind === "url") config.urls = addressLines;
  else if (kind === "crawl") {
    config.startUrls = startLines;
    config.depth = depth;
    config.maxItems = maxItemsValue;
    if (lines(include).length) config.include = lines(include);
    if (lines(exclude).length) config.exclude = lines(exclude);
  } else if (kind === "snapshot") config.collection = snapshotCollection;
  else {
    put("connectorKey", connectorKey);
    if (kind === "sharepoint") { put("siteUrl", siteUrl); put("library", library); } else put("user", user);
    put("folder", folder);
    config.recursive = recursive;
  }
  put("kind", docKind);
  put("issuer", issuer);
  const draft: KnowledgeSource = { key: effectiveKey, kind, name: name.trim() || null, config, schedule: schedule.trim() || null, enabled };
  const draftJson = JSON.stringify(draft);
  useEffect(() => setPreview(null), [draftJson]);

  const missing = kind === "url" ? addressLines.length === 0 || !!badAddress
    : kind === "crawl" ? !!startError || !!badAddress || !!maxItemsError
    : kind === "snapshot" ? !snapshotCollection
    : !connectorKey.trim() || (kind === "sharepoint" ? !/^https:\/\//i.test(siteUrl.trim()) : !user.trim());
  const ready = !keyError && !missing && !cronError;

  const test = async () => {
    try { setPreview(await actions.testSource.mutateAsync({ key: collectionKey, source: draft })); } catch (e) {
      // A source that could not be read is the test's own answer (succeeded: false), shown as the preview; any other failure is shown below with its cause.
      setPreview(isUnreadable(e) ? (e as SaveError).result as KnowledgeSourcePreview : null);
    }
  };
  const graphConnectors = (connectors.data ?? []).filter((c) => c.kind === "Rest").sort((a, b) => Number(b.tags.includes("microsoft-365")) - Number(a.tags.includes("microsoft-365")) || a.name.localeCompare(b.name));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{source ? t("admin:knowledge.sources.editTitle", { name: sourceLabel(source, t) }) : t("admin:knowledge.sources.add")}</DialogTitle>
          <DialogDescription>{t("admin:knowledge.sources.dialogIntro")}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {SYNCABLE_SOURCE_KINDS.map((k) => {
            const I = SOURCE_ICON[k];
            return (
              <button key={k} type="button" disabled={saved && k !== kind} aria-pressed={kind === k} onClick={() => setKind(k)}
                className={cn("flex items-start gap-2 rounded-xl border p-3 text-start text-xs transition-colors", kind === k ? "border-aegold-400 bg-aegold-50/60" : "border-sand-200 hover:bg-sand-50", saved && k !== kind && "cursor-not-allowed opacity-40")}>
                <I className="mt-0.5 size-4 shrink-0 text-aegold-700" />
                <span><span className="block font-medium text-foreground">{t(`admin:knowledge.sources.kind.${k}`)}</span><span className="text-muted-foreground">{t(`admin:knowledge.sources.kindHint.${k}`)}</span></span>
              </button>
            );
          })}
        </div>

        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
          <Field label={t("admin:common.name")}><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t(`admin:knowledge.sources.namePlaceholder.${kind}`)} autoFocus /></Field>
          <Field label={t("admin:common.key")} hint={saved ? t("admin:knowledge.sources.keyLocked") : undefined} error={keyError ?? undefined}>
            <Input value={effectiveKey} dir="ltr" className="font-mono" readOnly={saved} onChange={(e) => { setKeyTouched(true); setKey(e.target.value.toLowerCase()); }} />
          </Field>
          <Field label={t("admin:common.enabled")}><div className="flex h-10 items-center"><Switch checked={enabled} onCheckedChange={setEnabled} /></div></Field>
        </div>

        {kind === "url" ? (
          <Field label={t("admin:knowledge.sources.fields.urls")} hint={t("admin:knowledge.sources.fields.urlsHint")} error={badAddress ? t("admin:knowledge.sources.errors.address", { url: badAddress }) : undefined}>
            <Textarea value={urls} dir="ltr" onChange={(e) => setUrls(e.target.value)} className="min-h-[88px] font-mono text-xs" placeholder={"https://fcsc.gov.ae/en-us/Pages/Statistics/Methodology.aspx"} />
          </Field>
        ) : kind === "crawl" ? (
          <div className="grid gap-3">
            <Field label={t("admin:knowledge.sources.fields.startUrls")} hint={t("admin:knowledge.sources.fields.startUrlsHint")} required
              error={badAddress ? t("admin:knowledge.sources.errors.address", { url: badAddress }) : startUrls.trim() ? startError ?? undefined : undefined}>
              <Textarea value={startUrls} dir="ltr" onChange={(e) => setStartUrls(e.target.value)} className="min-h-[72px] font-mono text-xs" placeholder={"https://www.moet.gov.ae/en/reports"} />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t("admin:knowledge.sources.fields.depth")} hint={t("admin:knowledge.sources.fields.depthHint")}>
                <NativeSelect value={String(depth)} onChange={(e) => setDepth(Number(e.target.value))}>{[0, 1, 2].map((d) => <option key={d} value={d}>{d}</option>)}</NativeSelect>
              </Field>
              <Field label={t("admin:knowledge.sources.fields.maxItems")} hint={t("admin:knowledge.sources.fields.maxItemsHint")} error={maxItemsError ?? undefined}>
                <Input type="number" min={1} max={300} dir="ltr" value={maxItems} onChange={(e) => setMaxItems(e.target.value)} />
              </Field>
              <Field label={t("admin:knowledge.sources.fields.include")} hint={t("admin:knowledge.sources.fields.includeHint")}>
                <Textarea value={include} dir="ltr" onChange={(e) => setInclude(e.target.value)} className="min-h-[64px] font-mono text-xs" placeholder={"*.pdf\n/publications/"} />
              </Field>
              <Field label={t("admin:knowledge.sources.fields.exclude")} hint={t("admin:knowledge.sources.fields.excludeHint")}>
                <Textarea value={exclude} dir="ltr" onChange={(e) => setExclude(e.target.value)} className="min-h-[64px] font-mono text-xs" placeholder={"/news/\n*login*"} />
              </Field>
            </div>
          </div>
        ) : kind === "snapshot" ? (
          <Field label={t("admin:knowledge.sources.fields.snapshotCollection")} hint={t("admin:knowledge.sources.fields.snapshotCollectionHint")} required>
            <NativeSelect value={snapshotCollection} dir="ltr" className="font-mono" onChange={(e) => setSnapshotCollection(e.target.value)}>{SNAPSHOT_COLLECTIONS.map((c) => <option key={c} value={c}>{c}</option>)}</NativeSelect>
          </Field>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin:knowledge.sources.fields.connector")} hint={t("admin:knowledge.sources.fields.connectorHint")}>
              {connectors.isError
                ? <Input value={connectorKey} dir="ltr" className="font-mono" onChange={(e) => setConnectorKey(e.target.value)} />
                : <NativeSelect value={connectorKey} disabled={connectors.isLoading} onChange={(e) => setConnectorKey(e.target.value)}>{graphConnectors.some((c) => c.key === connectorKey) ? null : <option value={connectorKey}>{connectorKey}</option>}{graphConnectors.map((c) => <option key={c.key} value={c.key}>{locale === "ar" && c.nameAr ? c.nameAr : c.name}{c.enabled ? "" : ` (${t("admin:common.disabled")})`}</option>)}</NativeSelect>}
            </Field>
            {kind === "sharepoint" ? (
              <>
                <Field label={t("admin:knowledge.sources.fields.siteUrl")} required><Input value={siteUrl} dir="ltr" onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://moet.sharepoint.com/sites/LegalAffairs" /></Field>
                <Field label={t("admin:knowledge.sources.fields.library")} hint={t("admin:knowledge.sources.fields.libraryHint")}><Input value={library} onChange={(e) => setLibrary(e.target.value)} /></Field>
              </>
            ) : (
              <Field label={t("admin:knowledge.sources.fields.user")} hint={t("admin:knowledge.sources.fields.userHint")} required><Input value={user} dir="ltr" onChange={(e) => setUser(e.target.value)} placeholder="policy.unit@moet.gov.ae" /></Field>
            )}
            <Field label={t("admin:knowledge.sources.fields.folder")} hint={t("admin:knowledge.sources.fields.folderHint")}><Input value={folder} dir="ltr" onChange={(e) => setFolder(e.target.value)} placeholder="/Opinions register" /></Field>
            <Field label={t("admin:knowledge.sources.fields.recursive")}><div className="flex h-10 items-center gap-2 text-xs text-muted-foreground"><Switch checked={recursive} onCheckedChange={setRecursive} /> {t("admin:knowledge.sources.fields.recursiveHint")}</div></Field>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:knowledge.sources.fields.documentKind")}><NativeSelect value={docKind} onChange={(e) => setDocKind(e.target.value)}>{DOCUMENT_KINDS.map((k) => <option key={k} value={k}>{t(`enums.corpusKind.${k}`, { defaultValue: k })}</option>)}</NativeSelect></Field>
          <Field label={t("admin:knowledge.documents.issuer")} hint={t("admin:knowledge.sources.fields.issuerHint")}><Input value={issuer} onChange={(e) => setIssuer(e.target.value)} /></Field>
        </div>

        <Field label={t("admin:knowledge.sources.fields.schedule")} hint={t("admin:knowledge.sources.fields.scheduleHint")} error={cronError ?? undefined}>
          <div className="flex flex-wrap items-center gap-1.5">
            {[{ id: "manual", cron: "" }, ...SCHEDULE_PRESETS].map((p) => (
              <Button key={p.id} type="button" size="sm" variant={!custom && schedule === p.cron ? "gold" : "outline"} onClick={() => { setCustom(false); setSchedule(p.cron); }}>{t(`admin:knowledge.sources.schedule.${p.id}`)}</Button>
            ))}
            <Button type="button" size="sm" variant={custom ? "gold" : "outline"} onClick={() => setCustom(true)}>{t("admin:knowledge.sources.schedule.custom")}</Button>
            {custom ? <Input value={schedule} dir="ltr" className="h-8 w-40 font-mono text-xs" placeholder="30 2 * * 1-5" onChange={(e) => setSchedule(e.target.value)} /> : null}
          </div>
        </Field>

        {actions.testSource.error && !isUnreadable(actions.testSource.error) ? <InlineAlert tone="danger" title={t("admin:common.error")}>{saveFailureText(actions.testSource.error)}</InlineAlert> : null}
        {preview ? <PreviewList preview={preview} locale={locale} t={t} /> : null}

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" disabled={missing || !!keyError} loading={actions.testSource.isPending} onClick={() => void test()}><FlaskConical /> {t("admin:knowledge.sources.test")}</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>{t("admin:common.cancel")}</Button>
            <Button variant="gold" disabled={!ready} onClick={() => onSave(draft)}>{source ? t("admin:knowledge.sources.apply") : t("admin:knowledge.sources.add")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The test answered that the source could not be read (`succeeded: false`): its own result, not a failed request. */
function isUnreadable(e: unknown): boolean {
  return e instanceof SaveError && e.cause === "refused" && !!e.result;
}

function PreviewList({ preview, locale, t }: { preview: KnowledgeSourcePreview; locale: Locale; t: T }) {
  if (!preview.succeeded) return <InlineAlert tone="danger" title={t("admin:knowledge.sources.previewFailed")}>{preview.error}</InlineAlert>;
  const supported = preview.items.filter((i) => i.supported).length;
  return (
    <div className="min-w-0 rounded-xl border border-sand-200">
      <div className="flex flex-wrap items-center gap-2 border-b border-sand-200 bg-sand-50 px-3 py-2 text-xs">
        <CheckCircle2 className="size-4 text-success-600" />
        <span className="font-medium">{t("admin:knowledge.sources.previewSummary", { total: preview.total, supported })}</span>
        {preview.total > preview.items.length ? <span className="text-muted-foreground">{t("admin:knowledge.sources.previewFirst", { count: preview.items.length })}</span> : null}
      </div>
      <ul className="max-h-56 divide-y divide-sand-100 overflow-y-auto text-xs">
        {preview.items.map((i) => (
          <li key={i.externalId} className={cn("flex items-start gap-2 px-3 py-1.5", !i.supported && "opacity-60")}>
            {i.supported ? <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-success-600" /> : <MinusCircle className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />}
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{i.title}</div>
              <div className="truncate font-mono text-[10px] text-muted-foreground" dir="ltr">{i.note ?? i.url}</div>
            </div>
            {!i.supported ? <Badge variant="outline" size="sm">{t("admin:knowledge.sources.unsupported")}</Badge> : null}
            {i.modifiedAt ? <span className="shrink-0 text-[10px] text-muted-foreground">{formatRelative(i.modifiedAt, locale)}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
